import { PageHeader } from '@/shared/components/PageHeader';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';

interface LeaveEntry {
  id: string;
  userName: string;
  department: string;
  leaveType: 'Annual Leave' | 'Sick Leave' | 'Training' | 'Other';
  startDate: string;
  endDate: string;
  status: 'Approved' | 'Pending' | 'Rejected';
}

const MOCK_LEAVE_DATA: LeaveEntry[] = [
  {
    id: '1',
    userName: 'Andy Morris',
    department: 'Wastewater Treatment',
    leaveType: 'Annual Leave',
    startDate: '2026-03-24',
    endDate: '2026-03-28',
    status: 'Approved',
  },
  {
    id: '2',
    userName: 'Sarah Johnson',
    department: 'Field Services',
    leaveType: 'Annual Leave',
    startDate: '2026-03-25',
    endDate: '2026-03-25',
    status: 'Approved',
  },
  {
    id: '3',
    userName: 'Mike Davies',
    department: 'Operations',
    leaveType: 'Sick Leave',
    startDate: '2026-03-26',
    endDate: '2026-03-27',
    status: 'Approved',
  },
  {
    id: '4',
    userName: 'Lisa Parker',
    department: 'Quality Control',
    leaveType: 'Training',
    startDate: '2026-03-27',
    endDate: '2026-03-29',
    status: 'Approved',
  },
  {
    id: '5',
    userName: 'Jim Smith',
    department: 'Emergency Response',
    leaveType: 'Annual Leave',
    startDate: '2026-04-01',
    endDate: '2026-04-05',
    status: 'Pending',
  },
  {
    id: '6',
    userName: 'Emma Wilson',
    department: 'Field Services',
    leaveType: 'Annual Leave',
    startDate: '2026-04-03',
    endDate: '2026-04-04',
    status: 'Approved',
  },
  {
    id: '7',
    userName: 'David Chen',
    department: 'Wastewater Treatment',
    leaveType: 'Other',
    startDate: '2026-04-07',
    endDate: '2026-04-07',
    status: 'Pending',
  },
];

export default function LeaveCalendarPage() {
  const navigate = useNavigate();
  const [currentMonth, setCurrentMonth] = useState(new Date(2026, 2)); // March 2026
  const [departmentFilter, setDepartmentFilter] = useState('All Departments');
  const [leaveTypeFilter, setLeaveTypeFilter] = useState('All Types');

  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const previousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const isDateInRange = (date: Date, startDate: string, endDate: string) => {
    const checkDate = date.getTime();
    const start = new Date(startDate).getTime();
    const end = new Date(endDate).getTime();
    return checkDate >= start && checkDate <= end;
  };

  const getLeaveForDate = (date: Date) => {
    return MOCK_LEAVE_DATA.filter((leave) => {
      const matchesDepartment =
        departmentFilter === 'All Departments' || leave.department === departmentFilter;
      const matchesType = leaveTypeFilter === 'All Types' || leave.leaveType === leaveTypeFilter;
      const matchesDate = isDateInRange(date, leave.startDate, leave.endDate);

      return matchesDepartment && matchesType && matchesDate && leave.status === 'Approved';
    });
  };

  const getLeaveTypeBadgeColor = (type: LeaveEntry['leaveType']) => {
    switch (type) {
      case 'Annual Leave':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'Sick Leave':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'Training':
        return 'bg-purple-100 text-purple-800 border-purple-300';
      case 'Other':
        return 'bg-slate-100 text-slate-800 border-slate-300';
    }
  };

  const daysInMonth = getDaysInMonth(currentMonth);
  const firstDayOfMonth = getFirstDayOfMonth(currentMonth);
  const monthName = currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const calendarDays = [];
  for (let i = 0; i < firstDayOfMonth; i++) {
    calendarDays.push(null);
  }
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(day);
  }

  const filteredUpcomingLeave = MOCK_LEAVE_DATA.filter((leave) => {
    const matchesDepartment =
      departmentFilter === 'All Departments' || leave.department === departmentFilter;
    const matchesType = leaveTypeFilter === 'All Types' || leave.leaveType === leaveTypeFilter;
    const leaveDate = new Date(leave.startDate);
    const isUpcoming = leaveDate >= new Date();

    return matchesDepartment && matchesType && isUpcoming;
  }).sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <PageHeader
        title="Leave Calendar"
        subtitle="Team absence and leave tracking"
        icon={<CalendarIcon className="w-7 h-7" style={{ color: '#E84A2B' }} />}
        breadcrumbs={[
          { label: 'Home', href: '/', current: false },
          { label: 'Users', href: '/users', current: false },
          { label: 'Leave Calendar', current: true },
        ]}
        actions={
          <Button
            variant="outline"
            onClick={() => navigate('/users')}
            className="border-border hover:bg-accent rounded-lg"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back to Users
          </Button>
        }
      />

      <div className="flex-1 overflow-y-auto px-6 space-y-4">
        {/* Filters and Navigation */}
        <div className="bg-card border border-border rounded-lg shadow-sm p-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" onClick={previousMonth} className="rounded-lg">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <h2 className="text-lg font-semibold text-foreground min-w-[200px] text-center">
                {monthName}
              </h2>
              <Button variant="outline" size="sm" onClick={nextMonth} className="rounded-lg">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            <div className="flex gap-3">
              <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                <SelectTrigger className="w-[200px] rounded-lg border-border">
                  <SelectValue placeholder="All Departments" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All Departments">All Departments</SelectItem>
                  <SelectItem value="Wastewater Treatment">Wastewater Treatment</SelectItem>
                  <SelectItem value="Field Services">Field Services</SelectItem>
                  <SelectItem value="Operations">Operations</SelectItem>
                  <SelectItem value="Quality Control">Quality Control</SelectItem>
                  <SelectItem value="Emergency Response">Emergency Response</SelectItem>
                </SelectContent>
              </Select>

              <Select value={leaveTypeFilter} onValueChange={setLeaveTypeFilter}>
                <SelectTrigger className="w-[160px] rounded-lg border-border">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All Types">All Types</SelectItem>
                  <SelectItem value="Annual Leave">Annual Leave</SelectItem>
                  <SelectItem value="Sick Leave">Sick Leave</SelectItem>
                  <SelectItem value="Training">Training</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendar Grid */}
          <div className="lg:col-span-2">
            <Card className="bg-card border-border shadow-sm p-5">
              <div className="grid grid-cols-7 gap-2">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                  <div
                    key={day}
                    className="text-center text-xs font-semibold text-muted-foreground py-2"
                  >
                    {day}
                  </div>
                ))}

                {calendarDays.map((day, index) => {
                  if (day === null) {
                    return <div key={`empty-${index}`} className="aspect-square" />;
                  }

                  const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
                  const leaveEntries = getLeaveForDate(date);
                  const isToday = date.toDateString() === new Date().toDateString();

                  return (
                    <div
                      key={day}
                      className={`aspect-square border rounded-lg p-1 ${
                        isToday ? 'border-[#E84A2B] bg-orange-50' : 'border-border bg-background'
                      }`}
                    >
                      <div
                        className={`text-xs font-semibold mb-1 ${
                          isToday ? 'text-[#E84A2B]' : 'text-foreground'
                        }`}
                      >
                        {day}
                      </div>
                      <div className="space-y-0.5">
                        {leaveEntries.slice(0, 2).map((leave) => (
                          <div
                            key={leave.id}
                            className="text-[10px] px-1 py-0.5 rounded bg-blue-100 text-blue-800 truncate"
                            title={`${leave.userName} - ${leave.leaveType}`}
                          >
                            {leave.userName.split(' ')[0]}
                          </div>
                        ))}
                        {leaveEntries.length > 2 && (
                          <div className="text-[9px] text-muted-foreground text-center">
                            +{leaveEntries.length - 2} more
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          </div>

          {/* Upcoming Leave List */}
          <div>
            <Card className="bg-card border-border shadow-sm">
              <div className="p-4 border-b border-border">
                <h3 className="text-sm font-semibold text-foreground">Upcoming Leave</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  {filteredUpcomingLeave.length} upcoming absences
                </p>
              </div>
              <div className="max-h-[600px] overflow-y-auto">
                {filteredUpcomingLeave.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    <CalendarIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">No upcoming leave found</p>
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    {filteredUpcomingLeave.map((leave) => (
                      <div key={leave.id} className="p-4 hover:bg-accent/50 transition-colors">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="font-medium text-sm text-foreground">
                            {leave.userName}
                          </div>
                          <Badge
                            variant="outline"
                            className={`text-[10px] ${
                              leave.status === 'Approved'
                                ? 'bg-green-100 text-green-800 border-green-300'
                                : leave.status === 'Pending'
                                  ? 'bg-amber-100 text-amber-800 border-amber-300'
                                  : 'bg-red-100 text-red-800 border-red-300'
                            }`}
                          >
                            {leave.status}
                          </Badge>
                        </div>
                        <div className="text-xs text-muted-foreground mb-2">{leave.department}</div>
                        <Badge
                          variant="outline"
                          className={`text-xs mb-2 ${getLeaveTypeBadgeColor(leave.leaveType)}`}
                        >
                          {leave.leaveType}
                        </Badge>
                        <div className="text-xs text-foreground">
                          {new Date(leave.startDate).toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: 'short',
                          })}{' '}
                          -{' '}
                          {new Date(leave.endDate).toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Card>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-card border border-border rounded-lg p-4 shadow-sm">
            <div className="text-xs text-muted-foreground mb-1">Total Leave Requests</div>
            <div className="text-2xl font-bold text-foreground">{MOCK_LEAVE_DATA.length}</div>
          </div>
          <div className="bg-card border border-border rounded-lg p-4 shadow-sm">
            <div className="text-xs text-muted-foreground mb-1">Approved</div>
            <div className="text-2xl font-bold text-green-600">
              {MOCK_LEAVE_DATA.filter((l) => l.status === 'Approved').length}
            </div>
          </div>
          <div className="bg-card border border-border rounded-lg p-4 shadow-sm">
            <div className="text-xs text-muted-foreground mb-1">Pending</div>
            <div className="text-2xl font-bold text-amber-600">
              {MOCK_LEAVE_DATA.filter((l) => l.status === 'Pending').length}
            </div>
          </div>
          <div className="bg-card border border-border rounded-lg p-4 shadow-sm">
            <div className="text-xs text-muted-foreground mb-1">On Leave Today</div>
            <div className="text-2xl font-bold text-blue-600">
              {getLeaveForDate(new Date()).length}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
